// customCursorDraw — pure Canvas2D paint routine for the 24×24 custom cursor
// (custom-cursor-2026-09-19.md §2.2/§2.5), shared by every renderer:
// the editor grid, the 1x/2x editor preview, `BrushCursorOverlay` (self),
// `NameLabelOverlay` (peers), and the `MemberPanel` / social
// dock list thumbnails.
//
// Draws with per-pixel `fillRect` blocks rather than
// `ImageData`/`putImageData` — same idiom as `drawArgbCanvas.ts` (see that
// file's header): this package's vitest runs with no DOM by default, and
// jsdom (the opt-in DOM env for component mount tests) has no real Canvas2D
// backend, so pixel-buffer APIs are untestable at any level here. Plain
// `fillStyle`/`fillRect` calls, by contrast, can be recorded and asserted
// against a minimal fake `ctx` (see `customCursorDraw.test.ts`), and in a
// real browser they render identically to `putImageData` for a 24×24 image
// (24×24 = 576 fillRect calls at most, called at low frequency — well within
// budget, architecture.md §17).
//
// `image-rendering: pixelated` (crisp block edges at any CSS scale) is a
// *caller* concern (`CustomCursorCanvas.tsx` sets it on the `<canvas>`
// element) — this module never touches the DOM beyond the passed-in ctx.

import { CURSOR_SIZE } from '@oekaki/protocol';

import { getCursorPixel } from './customCursorPixels.js';

/**
 * Screen-px offset (both axes) from the stroke tip / pointer position to the
 * custom cursor thumbnail's **top-left corner** — the same convention as an
 * OS mouse cursor's hotspot, just inverted (the *pointer* is the hotspot, the
 * thumbnail's top-left sits `+N,+N` away from it) so the thumbnail never
 * covers the tip itself.
 *
 * 作者 staging フィードバック 2026-09-19 follow-up 「表示位置を自分と相手で
 * 統一したい」: 元々 self (`BrushCursorOverlay`, 旧 `OWN_CURSOR_THUMB_OFFSET_PX`
 * = 10) は「ブラシ円の右下」、peer (`NameLabelOverlay`) は「名前ラベルの左」と
 * 別々のアンカーで実装されていて、見える位置が食い違っていた。両方がこの 1 つの
 * 定数を使うことで「ペン先の右下 `(+N, +N)`」に統一する。screen px 固定
 * (zoom で拡縮しない、名前ラベルの `LABEL_VERTICAL_OFFSET_PX` と同じ流儀)。
 */
export const CUSTOM_CURSOR_HOTSPOT_OFFSET_PX = 6;

/**
 * Minimal Canvas2D surface this module needs (mirrors `drawArgbCanvas.ts`).
 * `fillStyle`'s type matches the real `CanvasRenderingContext2D` (a union
 * with `CanvasGradient`/`CanvasPattern`) purely so a real context is
 * assignable here — this module only ever *sets* plain `string` values.
 */
export interface CursorPaintContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, w: number, h: number): void;
  clearRect(x: number, y: number, w: number, h: number): void;
}

/** Checkerboard square size (canvas-buffer px) used to visualize transparency. */
export const CHECKER_SQUARE_PX = 4;
const CHECKER_LIGHT = '#ffffff';
const CHECKER_DARK = '#d8d8d8';
/** Editor-only cell gridline color (skipped when `grid` is false / omitted). */
const GRID_LINE_COLOR = 'rgba(0, 0, 0, 0.15)';

export interface DrawCustomCursorOptions {
  /** Size of one cursor pixel, in canvas-buffer px. Must be > 0. */
  readonly cellPx: number;
  /**
   * Paint a checkerboard behind transparent pixels (editor / preview use —
   * makes alpha=0 legible). Thumbnails (overlay/list) normally omit this so
   * the cursor floats over whatever is beneath it on screen.
   */
  readonly checker?: boolean;
  /** Draw 1px cell gridlines (editor grid use only). */
  readonly grid?: boolean;
}

/**
 * Paint the full 24×24 buffer onto `ctx`, starting at canvas-buffer origin
 * (0, 0). Callers that need an offset should translate the ctx themselves
 * (kept out of this pure function to match `drawArgbCanvas.ts`'s style).
 */
export const drawCustomCursor = (
  ctx: CursorPaintContext,
  pixels: Uint8ClampedArray,
  opts: DrawCustomCursorOptions,
): void => {
  const cellPx = Number.isFinite(opts.cellPx) && opts.cellPx > 0 ? opts.cellPx : 1;
  const sizePx = cellPx * CURSOR_SIZE;

  ctx.clearRect(0, 0, sizePx, sizePx);

  if (opts.checker === true) {
    drawChecker(ctx, sizePx);
  }

  for (let y = 0; y < CURSOR_SIZE; y++) {
    for (let x = 0; x < CURSOR_SIZE; x++) {
      const { r, g, b, a } = getCursorPixel(pixels, x, y);
      if (a === 0) continue;
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
      ctx.fillRect(x * cellPx, y * cellPx, cellPx, cellPx);
    }
  }

  if (opts.grid === true) {
    drawGrid(ctx, cellPx, sizePx);
  }
};

const drawChecker = (ctx: CursorPaintContext, sizePx: number): void => {
  const cols = Math.ceil(sizePx / CHECKER_SQUARE_PX);
  for (let row = 0; row * CHECKER_SQUARE_PX < sizePx; row++) {
    for (let col = 0; col < cols; col++) {
      ctx.fillStyle = (row + col) % 2 === 0 ? CHECKER_LIGHT : CHECKER_DARK;
      ctx.fillRect(
        col * CHECKER_SQUARE_PX,
        row * CHECKER_SQUARE_PX,
        CHECKER_SQUARE_PX,
        CHECKER_SQUARE_PX,
      );
    }
  }
};

const drawGrid = (ctx: CursorPaintContext, cellPx: number, sizePx: number): void => {
  ctx.fillStyle = GRID_LINE_COLOR;
  for (let i = 0; i <= CURSOR_SIZE; i++) {
    const p = i * cellPx;
    ctx.fillRect(p, 0, 1, sizePx);
    ctx.fillRect(0, p, sizePx, 1);
  }
};
